import { Button, buttonVariants } from "@workspace/web-ui/components/button";
import { Image } from "@workspace/web-ui/components/image";
import { useEffect, useState } from "react";
import { AppointmentModal } from "../drawers/appointment-modal";

import { FormattedEncounter } from "../../../encounters/types";
import { EncounterStatus } from "../../../encounters/components/encounter-status";
import { cn } from "@workspace/web-ui/lib/utils";
import { getRouteApi } from "@tanstack/react-router";
import { Info } from "lucide-react";

interface InfoRowProps {
  icon: string;
  alt: string;
  value: string;
  testId?: string;
}

const InfoRow = ({ icon, alt, value, testId }: InfoRowProps) => (
  <div className="flex flex-row gap-2 items-center" data-testid={testId}>
    <Image src={icon} alt={alt} className="w-4 h-4" layout="constrained" width={96} height={96} />
    <span className="text-sm font-normal text-[#202020CC]">{value}</span>
  </div>
);

interface ActionButtonsProps {
  onViewLogs?: () => void;
  meetingLink?: string;
  isMeetingActive?: boolean;
  hasMeetingPassed?: boolean;
}

const ActionButtons = ({ onViewLogs, meetingLink, isMeetingActive, hasMeetingPassed }: ActionButtonsProps) => {
  const isDisabled = !meetingLink || !isMeetingActive;
  const showMeetingLink = meetingLink && !hasMeetingPassed;

  return (
    <div className="flex gap-2 items-center w-full md:w-fit" data-testid="action-buttons">
      <Button
        onClick={onViewLogs}
        className="button-primary h-fit w-full"
        aria-label="View patient logs"
      >
        View Details
      </Button>
      {showMeetingLink && (
        <a
          target="_blank" rel="noopener noreferrer"
          href={meetingLink}
          className={cn(buttonVariants({ variant: "default" }), "rounded-[8px] !bg-[#4272DD] !text-white !hover:bg-[#3661C5] transition-colors button-primary !shadow-none !border-none h-fit", isDisabled ? "pointer-events-none cursor-not-allowed opacity-60 no-underline" : "")}
          aria-label="Join meeting"
          aria-disabled={isDisabled}
        >
          Join Meeting
        </a>
      )}
    </div>
  );
};

type AppointmentCardProps = {
  item: FormattedEncounter
  meetingLink: string
  program: string
}

const AppointmentCard = ({
  item,
  meetingLink,
  program
}: AppointmentCardProps) => {
  const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
  const navigate = routeApi.useNavigate();
  const search = routeApi.useSearch() as { encounter_id?: string };
  const encounter_id = search.encounter_id;
  const { date, time, status, tag, subtitle, patient, id } = item

  const [isViewDetailsModalOpen, setIsViewDetailsModalOpen] = useState(false);

  // Check if this encounter matches the encounter_id in URL params
  useEffect(() => {
    if (encounter_id === id) {
      setIsViewDetailsModalOpen(true);
    }
  }, [encounter_id, id]);

  const meetingInfo = [
    { icon: "/icons/meeting-now.svg", alt: "calendar", value: date, testId: "meeting-date" },
    { icon: "/icons/clock.svg", alt: "clock", value: time, testId: "meeting-time" },
  ];

  const handleViewDetails = () => {
    navigate({
      search: { ...search, encounter_id: id },
    });
  };

  const handleModalClose = (open: boolean) => {
    setIsViewDetailsModalOpen(open);
    if (!open) {
      // Clear the encounter_id from URL when modal is closed
      navigate({
        search: { ...search, encounter_id: undefined },
      });
    }
  };

  const isMeetingActive = status === "happening";
  // const hasPendingTasks = Array.isArray(item.recommendedTasks) && item.recommendedTasks.some(task => task?.status === "pending");

  // Check if meeting has passed by combining date and time
  const hasMeetingPassed = (() => {
    try {
      // Assuming date format is like "Dec 24, 2024" and time is like "2:30 PM"
      const meetingDateTime = new Date(`${date} ${time}`);
      const now = new Date();
      return meetingDateTime < now;
    } catch (error) {
      // If date parsing fails, don't hide the link
      console.warn("Failed to parse meeting date/time:", { date, time }, error);
      return false;
    }
  })();


  return (
    <>
      <article
        className="flex md:flex-row flex-col gap-4 p-4 border border-[#E6E6E6] border-l-[6px] border-l-[#4272DD] shadow-sm bg-white justify-between items-start w-full"
        data-testid="appointment-card"
      >
        <div className="flex flex-col gap-3 w-full">
          <div className="flex items-center gap-2">
            <EncounterStatus status={status} tag={tag} />
            {hasMeetingPassed && (
              <div
                className={cn(
                  "flex flex-row gap-1 items-center rounded-full px-[6px] py-[4px] w-fit",
                  "text-[#FFA726] bg-[#FFF5E6]"
                )}
                data-testid="action-needed-tag"
              >
                <Info className="size-4 text-[#FFA726]" />
                <span className="text-xs font-medium">Action Needed</span>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-4 md:flex-row items-start">
            <div className="flex flex-col gap-3 w-full">
              <div className="flex flex-row gap-2 sm:gap-3 sm:items-center items-start">
                <h2
                  className="text-[20px] leading-normal font-semibold text-[#2D2F35]"
                  data-testid="appointment-title"
                >
                  {patient.name} {subtitle ? subtitle : "Appointment"}
                </h2>
                {program === "urgent-care" ? (
                  item.patient.urgent_care_onboarded && item.patient.identity_verified && (
                    <div className="flex-shrink-0 mt-1 sm:mt-0">
                      <Image src="/icons/check.svg"
                        alt="timeline"
                        className="w-4 h-4 sm:w-5 sm:h-5" layout="constrained" width={96} height={96} />
                    </div>
                  )
                ) : (
                  item.patient.medical_plan?.payment_status === "paid" &&
                  item.patient.glp1_guide_accepted &&
                  item.patient.identity_verified &&
                  item.patient.supplementary_health_assessment &&
                  Object.keys(item.patient.supplementary_health_assessment).length > 0 && (
                    <div className="flex-shrink-0 mt-1 sm:mt-0">
                      <Image src="/icons/check.svg"
                        alt="timeline"
                        className="w-4 h-4 sm:w-5 sm:h-5" layout="constrained" width={96} height={96} />
                    </div>
                  )
                )}
              </div>

              <div className="flex flex-col gap-2 items-start">
                <div className="flex gap-2 items-start">
                {meetingInfo.map((info) => (
                  <InfoRow
                    key={info.testId}
                    icon={info.icon}
                    alt={info.alt}
                    value={info.value}
                    testId={info.testId}
                  />
                ))}
                </div>
                {program === "urgent-care" && (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center text-sm font-normal text-[#202020CC] gap-2 flex-wrap">
                      {patient.age !== undefined && (
                        <>
                          <span>{patient.age} years old</span>
                        </>
                      )}
                      {patient.gender && (
                        <>
                          <span className="mx-2">•</span>
                          <span>{patient.gender}</span>
                        </>
                      )}
                    </div>
                    <p className="text-sm font-normal text-[#202020CC]">
                      <span className="font-semibold">Diagnosis:</span> {item.session?.diagnosis?.[0]?.disease_name ?? "No diagnosis available"}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <ActionButtons
              onViewLogs={handleViewDetails}
              meetingLink={meetingLink}
              isMeetingActive={isMeetingActive}
              hasMeetingPassed={hasMeetingPassed}
            />
          </div>
        </div>

      </article>

      {isViewDetailsModalOpen && item && (
        <AppointmentModal
          open={isViewDetailsModalOpen}
          onOpenChange={handleModalClose}
          encounter={item}
          type={item.type}
        />
      )}
    </>
  );
};

export default AppointmentCard;